"use client";
import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Pagination from "../shared/paginator/paginator.js";
import {
  Search,
  TrendingUp,
  Clock,
  User,
  ExternalLink,
  Filter,
  X,
  BookOpen,
  Briefcase,
  Target,
  AlertCircle,
  Star,
  Award,
  PlayCircle,
  Send,
  Info,
  Loader2,
  Download,
  ChevronDown,
} from "lucide-react";
import "./UpskillPage.css";
import { useTranslation } from "react-i18next";
import NoDataFound from "../shared/NoDataFound.js";
import Modal from "../layout/Modal";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackButton from "../shared/backButton";
import EmptyState from "../shared/EmptyState";
import FilterNoResults from "../shared/FilterNoResults";
import { downloadLearningPathPdfApi } from "../../services/peopleService";
// import BulkAction, { BulkActionOption } from "../shared/utils/bulkAction";
import {
  getUpSkillingApi,
  getLearningPathApi,
} from "../../services/upskillingService";
import { shareLearningPathInsidePeople } from "../../services/peopleService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import CustomReadinessFilter from "../shared/CustomReadinessFilter";
import { getDepartmentsListApi } from "../../services/settingsService";
import ViewMoreModal from "../shared/ViewMoreModal";
import MessageManager from "../layout/Message.js";

const UpskillPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValueUserId = searchParams.get("userId");
  console.log("UpskillPage paramValueUserId:", paramValueUserId);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [showLearningPathModal, setShowLearningPathModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const { t, i18n } = useTranslation();

  const navigator = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("All");
  const normalizeUrgency = (list) => {
    const cleaned = Array.from(
      new Set((list || []).map((s) => s?.trim()).filter(Boolean)),
    );

    // if any real urgency exists, drop "All"
    const hasReal = cleaned.some((x) => x !== "All");
    if (hasReal) return cleaned.filter((x) => x !== "All");

    return cleaned.length ? cleaned : ["All"];
  };

  const initialUrgencyFromUrl = normalizeUrgency(
    searchParams.getAll("urgency"),
  );

  const [filterUrgency, setFilterUrgency] = useState(initialUrgencyFromUrl);
  // const [filterUrgency, setFilterUrgency] = useState(["All"]);
  const [sortBy, setSortBy] = useState("Urgency");
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    person: "",
  });

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterDepartment("All");
    setFilterUrgency(["All"]);
    setSortBy("Urgency");
    setShowSearchBar(false);
    setShowFiltersPanel(false);
    setCurrentPage(1);
  };

  const [upskillData, setUpskillData] = useState([]);

  const URGENCY_API_VALUES = {
    ALL: "All",
    CRITICAL: "Critical",
    HIGH: "High",
    MODERATE: "Moderate",
    LOW: "Low",
  };

  const urgencyOptions = [
    {
      value: URGENCY_API_VALUES.ALL,
      label: t("common.all"),
    },
    {
      value: URGENCY_API_VALUES.CRITICAL,
      label: t("upskillPage.urgencyLevels.critical"),
    },
    {
      value: URGENCY_API_VALUES.HIGH,
      label: t("upskillPage.urgencyLevels.high"),
    },
    {
      value: URGENCY_API_VALUES.MODERATE,
      label: t("upskillPage.urgencyLevels.moderate"),
    },
    {
      value: URGENCY_API_VALUES.LOW,
      label: t("upskillPage.urgencyLevels.low"),
    },
  ];

  const urgencyLevels = urgencyOptions.map((option) => option.label);

  const getUrgencyApiValue = (selectedValue) => {
    const normalizedValue = String(selectedValue || "").trim();

    // Already an English API value
    const englishOption = urgencyOptions.find(
      (option) => option.value === normalizedValue,
    );

    if (englishOption) {
      return englishOption.value;
    }

    // Translated UI label
    const translatedOption = urgencyOptions.find(
      (option) => option.label === normalizedValue,
    );

    return translatedOption?.value || null;
  };

  const getUrgencyDisplayLabel = (apiValue) => {
    return (
      urgencyOptions.find((option) => option.value === apiValue)?.label ||
      apiValue
    );
  };

  const getEnglishUrgencyValues = (values = []) => {
    const convertedValues = values.map(getUrgencyApiValue).filter(Boolean);

    const uniqueValues = [...new Set(convertedValues)];

    // When any real urgency exists, do not send All.
    const actualUrgencies = uniqueValues.filter((value) => value !== "All");

    return actualUrgencies.length > 0 ? actualUrgencies : ["All"];
  };

  const UpskillUrgencyTooltip = (option) => {
    const urgencyValue = getUrgencyApiValue(option);

    if (urgencyValue === "Critical") {
      return t("upskillPage.tooltips.filterTooltip.critical");
    }

    if (urgencyValue === "High") {
      return t("upskillPage.tooltips.filterTooltip.high");
    }

    if (urgencyValue === "Moderate") {
      return t("upskillPage.tooltips.filterTooltip.moderate");
    }

    if (urgencyValue === "Low") {
      return t("upskillPage.tooltips.filterTooltip.low");
    }

    return "";
  };
  const sortOptions = [
    { value: "Name", label: t("common.name") },
    { value: "Department", label: t("common.department") },
    { value: "Urgency", label: t("common.urgency") },
  ];

  const bulkOptions = [
    {
      value: "Share Learning Path",
      label: t("upskillPage.bulkActions.shareLearningPath"),
    },
    {
      value: "Download PDF",
      label: t("upskillPage.bulkActions.downloadPdf"),
    },
  ];

  const options = [
    {
      value: "share-learning-path",
      label: t("upskillPage.bulkActions.shareLearningPath"),
    },
    { value: "download-pdf", label: t("upskillPage.bulkActions.downloadPdf") },
  ];
  // const [selectedIds, setSelectedIds] = React.useState([]);

  // === Bulk select state  ===
  const [selectedIds, setSelectedIds] = useState([]); // array of userIds
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const getId = (p) => p._id;

  const currentPageIds = (upskillData || []).map(getId);

  // Smart search & filter toggles (like Employees page)
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterDepartment && filterDepartment !== "All") count++;
    if (filterUrgency && filterUrgency.length && !filterUrgency.includes("All"))
      count++;
    if (sortBy && sortBy !== "Urgency") count++;
    return count;
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const handleAction = async (action, ids) => {
    console.log("Bulk action:", action, ids);
    switch (action) {
      case "share-learning-path":
        // call your API for sharing
        // await api.learningPaths.share({ ids });
        break;
      case "download-pdf":
        // await api.learningPaths.downloadPdf({ ids });
        break;
      default:
        console.warn("Unknown action:", action);
    }
  };

  const toggleSelectAll = async (checked) => {
    if (checked) {
      try {
        setSelectAllLoading(true);
        const ids = await fetchAllMatchingIds();
        setSelectedIds(ids); // select ALL matching ids
      } catch (err) {
        console.error("Select-all fetch failed:", err);
        handleApiError(err, { showModal: true });
      } finally {
        setSelectAllLoading(false);
      }
    } else {
      setSelectedIds([]); // clear ALL selections
    }
  };
  // helper
  const deselect = (idsToClear) =>
    setSelectedIds((prev) => prev.filter((id) => !idsToClear.includes(id)));

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) =>
      checked
        ? Array.from(new Set([...prev, id]))
        : prev.filter((x) => x !== id),
    );
  };
  // Build SAME query you use for the list (filters must match)
  const buildUpskillQuery = (overrides = {}) => ({
    searchFilter: searchTerm,
    department: filterDepartment,
    urgency: getEnglishUrgencyValues(filterUrgency),
    sortBy,
    userId: paramValueUserId,
    ...overrides,
  });
  // Fetch ALL matching ids (across pages) using getUpSkillingApi
  const fetchAllMatchingIds = async () => {
    const pageSizeForBulk = 1000;

    let pageNo = 1;
    let allIds = [];
    let total = null;

    while (true) {
      const response = await getUpSkillingApi(
        buildUpskillQuery({
          pageNo,
          pageSize: pageSizeForBulk,
        }),
      );

      const normalizedResponse = normalizeUpskillResponse(response);

      const records = normalizedResponse.records;

      const batch = records.map((person) => person?._id).filter(Boolean);

      allIds = [...allIds, ...batch];

      if (total === null) {
        total = normalizedResponse.totalCount;
      }

      if (batch.length === 0) {
        break;
      }

      if (total !== null && allIds.length >= total) {
        break;
      }

      if (batch.length < pageSizeForBulk) {
        break;
      }

      pageNo += 1;
    }

    return [...new Set(allIds)];
  };
  const sortData = (data = []) => {
    return [...data].sort((a, b) => {
      switch (sortBy) {
        case "Name":
          return String(a?.name || "").localeCompare(String(b?.name || ""));

        case "Department":
          return String(a?.department || "").localeCompare(
            String(b?.department || ""),
          );

        case "Urgency":
          // Lower skillScore means more urgent.
          return Number(a?.skillScore ?? 999) - Number(b?.skillScore ?? 999);

        default:
          return 0;
      }
    });
  };

  const getUrgencyColor = (urgency) => {
    if (urgency >= 0 && urgency <= 10) {
      return "up-urgency-critical"; // Red
    } else if (urgency >= 11 && urgency <= 40) {
      return "up-urgency-high"; // Orange
    } else if (urgency >= 41 && urgency <= 70) {
      return "up-urgency-moderate"; // Yellow
    } else {
      return "up-urgency-low"; // Green
    }
  };

  const getUrgencyCriteria = (urgency) => {
    if (urgency >= 0 && urgency <= 10) {
      return t("upskillPage.tooltips.priority.critical.title").split(" ")[1];
    } else if (urgency >= 11 && urgency <= 40) {
      return t("upskillPage.tooltips.priority.high.title").split(" ")[1];
    } else if (urgency >= 41 && urgency <= 70) {
      return t("upskillPage.tooltips.priority.moderate.title").split(" ")[1];
    } else {
      return t("upskillPage.tooltips.priority.low.title").split(" ")[1];
    }
  };

  const getPriorityTooltip = (priority) => {
    if (priority >= 0 && priority <= 10) {
      return {
        title: t("upskillPage.tooltips.priority.critical.title"),
        message: t("upskillPage.tooltips.priority.critical.message"),
        details: t("upskillPage.tooltips.priority.critical.details"),
        action: t("upskillPage.tooltips.priority.critical.action"),
      };
    } else if (priority >= 11 && priority <= 40) {
      return {
        title: t("upskillPage.tooltips.priority.high.title"),
        message: t("upskillPage.tooltips.priority.high.message"),
        details: t("upskillPage.tooltips.priority.high.details"),
        action: t("upskillPage.tooltips.priority.high.action"),
      };
    } else if (priority >= 41 && priority <= 70) {
      return {
        title: t("upskillPage.tooltips.priority.moderate.title"),
        message: t("upskillPage.tooltips.priority.moderate.message"),
        details: t("upskillPage.tooltips.priority.moderate.details"),
        action: t("upskillPage.tooltips.priority.moderate.action"),
      };
    } else if (priority > 70) {
      return {
        title: t("upskillPage.tooltips.priority.low.title"),
        message: t("upskillPage.tooltips.priority.low.message"),
        details: t("upskillPage.tooltips.priority.low.details"),
        action: t("upskillPage.tooltips.priority.low.action"),
      };
    }
  };

  // const handleDownloadPDF = () => {
  //   const htmlString = generatePDFContent();
  //   const container = document.createElement("div");
  //   container.innerHTML = htmlString;
  //   document.body.appendChild(container);

  //   html2pdf()
  //     .from(container)
  //     .set({
  //       margin: [0.5, 0.5, 0.5, 0.5], // [top, left, bottom, right] in inches
  //       filename: `${selectedPerson.name.replace(
  //         /\s+/g,
  //         "_"
  //       )}_Learning_Path.pdf`,
  //       image: { type: "jpeg", quality: 0.98 },
  //       html2canvas: { scale: 2, useCORS: true },
  //       jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
  //     })
  //     .save()
  //     .then(() => {
  //       document.body.removeChild(container);
  //     });
  // };

  const generatePDFContent = () => {
    const learningPath = mockLearningPath;

    return `<!DOCTYPE html>
<html>
<head>
<title>${t("upskillPage.learningPathModal.title")} - ${
      selectedPerson.name
    }</title>
<style>
    @import url('https://fonts.googleapis.com/css2?family=Titillium+Web:wght@400;600;700&display=swap');
    body {
        font-family: 'Titillium Web', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        margin: 0;
        padding: 20px;
        background: #f9fafb;
        color: #333;
    }
    .header {
        background: linear-gradient(17deg, #0e1822 12.05%, #122d44 88.83%);
        padding: 30px 20px;
        text-align: center;
        color: white;
        border-radius: 12px;
        margin-bottom: 30px;
    }
    .header h1 {
        font-size: 32px;
        margin-bottom: 10px;
    }
    .header h2 {
        font-size: 22px;
        margin-bottom: 5px;
    }
    .header p {
        font-size: 14px;
        color: #cbd5e1;
    }
    .skill-section {
        margin-bottom: 40px;
        page-break-inside: avoid;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    }
    .skill-header {
        background: linear-gradient(17deg, #0e1822 12.05%, #122d44 88.83%);
        color: white;
        padding: 20px;
    }
    .skill-header h3 {
        margin: 0;
        font-size: 20px;
    }
    .skill-type {
        display: inline-block;
        background: rgba(255,255,255,0.2);
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 12px;
        margin-left: 10px;
    }
    .skill-content {
        padding: 25px;
        background: #ffffff;
    }
    .courses-section {
        margin-bottom: 25px;
    }
    .courses-section h4 {
        margin-bottom: 10px;
    }
    .course-item {
        background: #f1f5f9;
        padding: 15px;
        margin: 10px 0;
        border-left: 4px solid #10b981;
        border-radius: 8px;
    }
    .course-match {
        display: inline-block;
        margin-left: 10px;
        font-size: 12px;
        padding: 2px 8px;
        border-radius: 12px;
    }
    .match-true {
        background: #d1fae5;
        color: #065f46;
    }
    .match-false {
        background: #fee2e2;
        color: #991b1b;
    }
    .steps-section {
        margin-top: 25px;
    }
    .steps-section h4 {
        margin-bottom: 10px;
    }
    .step-item {
        margin: 15px 0;
        padding: 15px;
        border-left: 4px solid #f59e0b;
        background: #fffbeb;
        border-radius: 6px;
    }
    .step-stage {
        font-weight: bold;
        color: #92400e;
        margin-bottom: 8px;
        text-transform: uppercase;
        font-size: 14px;
    }
    .note-section {
        background: #fef3c7;
        border: 1px solid #f59e0b;
        padding: 15px;
        border-radius: 8px;
        margin-top: 15px;
    }
</style>
</head>
<body>
<div class="header">
    <h1>${t("upskillPage.learningPathModal.title")}</h1>
    <h2>${selectedPerson.name}</h2>
    <p>${t("upskillPage.learningPathModal.description", {
      name: selectedPerson.name,
    })}</p>
</div>

${learningPath
  .map(
    (skill) => `

    <div class="skill-section">
        <div class="skill-header">
            <h3>${skill.skill_gap}<span class="skill-type">${
              skill.type
            }</span></h3>
        </div>
        <div class="skill-content">
            ${
              skill.recommended_courses.length > 0
                ? `
            <div class="courses-section">
                <h4>${t("upskillPage.jobSkills")}</h4>
                ${skill.recommended_courses
                  .map(
                    (course) => `
                <div class="course-item">
                <strong>${course.course_name}</strong> (${t("common.id")}: ${course.course_id})
                    <span class="course-match ${
                      course.learning_method_match
                        ? "match-true"
                        : "match-false"
                    }">
                        ${t(
                          "upskillPage.trainingModal.platformPlaceholder",
                        )}: ${
                          course.learning_method_match
                            ? t("common.yes")
                            : t("common.no")
                        }
                    </span>
                    <span class="course-match ${
                      course.expertise_level_match
                        ? "match-true"
                        : "match-false"
                    }">
                        ${t("upskillPage.levels.level")}: ${
                          course.expertise_level_match
                            ? t("common.yes")
                            : t("common.no")
                        }
                    </span>
                </div>
                `,
                  )
                  .join("")}
            </div>
            `
                : ""
            }

            <div class="steps-section">
                <h4>${t("upskillPage.learningPathModal.learningJourney")}</h4>
                ${skill.steps
                  .map(
                    (step) => `
                <div class="step-item">
                    <div class="step-stage">${step.stage}</div>
                    <div>${step.description}</div>
                </div>
                `,
                  )
                  .join("")}
            </div>

            ${
              skill.note
                ? `
            <div class="note-section">
                <strong>📝 ${t(
                  "upskillPage.learningPathModal.importantNote",
                )}:</strong> ${skill.note}
            </div>
            `
                : ""
            }
        </div>
    </div>
`,
  )
  .join("")}
</body>
</html>`;
  };

  const handlePersonClick = (person) => {
    console.log("Person clicked:", person);
    navigator(`/employees/${person._id}`);
  };
  const handleUrgencySelect = (selectedList = [], selectedItem) => {
    const selectedApiValue = getUrgencyApiValue(selectedItem);

    if (selectedApiValue === "All") {
      setFilterUrgency(["All"]);
      setCurrentPage(1);
      return;
    }

    const englishValues = selectedList
      .map(getUrgencyApiValue)
      .filter((value) => value && value !== "All");

    setFilterUrgency(
      englishValues.length > 0 ? [...new Set(englishValues)] : ["All"],
    );

    setCurrentPage(1);
  };

  const handleUrgencyRemove = (selectedList = []) => {
    const englishValues = selectedList
      .map(getUrgencyApiValue)
      .filter((value) => value && value !== "All");

    setFilterUrgency(
      englishValues.length > 0 ? [...new Set(englishValues)] : ["All"],
    );

    setCurrentPage(1);
  };
  const CustomTooltip = ({ tooltip, children, id }) => {
    const triggerRef = useRef(null);
    const [coords, setCoords] = useState(null);
    const [open, setOpen] = useState(false);

    const updatePosition = () => {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const tooltipWidth = Math.min(320, viewportWidth - 24);
      const rawLeft = rect.left + rect.width / 2;

      const safeLeft = Math.min(
        Math.max(rawLeft, tooltipWidth / 2 + 12),
        viewportWidth - tooltipWidth / 2 - 12,
      );

      setCoords({
        top: rect.bottom + 8,
        left: safeLeft,
        width: tooltipWidth,
      });
    };

    useEffect(() => {
      if (!open) return;
      const onScrollOrResize = () => updatePosition();
      window.addEventListener("scroll", onScrollOrResize, true);
      window.addEventListener("resize", onScrollOrResize);
      updatePosition();
      return () => {
        window.removeEventListener("scroll", onScrollOrResize, true);
        window.removeEventListener("resize", onScrollOrResize);
      };
    }, [open]);

    return (
      <>
        <div
          ref={triggerRef}
          className="up-tooltip-container"
          onMouseEnter={() => {
            setOpen(true);
            // if you're using activeTooltip elsewhere, keep this next line
            // setActiveTooltip(id);
          }}
          onMouseLeave={() => {
            setOpen(false);
            // setActiveTooltip(null);
          }}
        >
          {children}
        </div>

        {open &&
          coords &&
          createPortal(
            <div
              className="up-custom-tooltip"
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                width: coords.width,
                transform: "translateX(-50%)",
              }}
            >
              <div className="up-tooltip-header">
                <strong>{tooltip.title}</strong>
              </div>
              <div className="up-tooltip-message">{tooltip.message}</div>
              {tooltip.details && (
                <div className="up-tooltip-details">{tooltip.details}</div>
              )}
              {tooltip.action && (
                <div className="up-tooltip-action">💡 {tooltip.action}</div>
              )}
              <div className="up-tooltip-arrow" />
            </div>,
            document.body,
          )}
      </>
    );
  };

  const [show, setShow] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [ViewMoreModalTitle, setViewMoreModalTitle] = useState("");

  const handleViewMore = (
    skills,
    title = t("upskillPage.viewMore.jobSkillsTitle"),
  ) => {
    setSelectedSkills(skills);
    setShow(true);
    setViewMoreModalTitle(title);
  };

  const renderSkillsWithLimit = (skills = [], limit = 3) => {
    if (!Array.isArray(skills) || skills.length === 0) {
      return (
        <span style={{ fontStyle: "italic", opacity: 0.7, fontSize: "12px" }}>
          {t("common.noneIdentified")}
        </span>
      );
    }

    const displayedSkills = skills.slice(0, limit);
    const remainingCount = skills.length - limit;

    return (
      <>
        {displayedSkills.map((skill, index) => (
          <span key={index} className="upskill-job-skill-tag-unique">
            {skill}
          </span>
        ))}
        {remainingCount > 0 && (
          <span
            className="upskill-job-skill-tag-unique"
            style={{ fontStyle: "italic", opacity: 0.7 }}
          >
            +{remainingCount} {t("employees.results.more")}
          </span>
        )}
      </>
    );
  };

  const renderSoftSkillsWithLimit = (skills = [], limit = 3) => {
    if (!Array.isArray(skills) || skills.length === 0) {
      return (
        <span style={{ fontStyle: "italic", opacity: 0.7, fontSize: "12px" }}>
          {t("common.noneIdentified")}
        </span>
      );
    }

    const displayedSkills = skills.slice(0, limit);
    const remainingCount = skills.length - limit;

    return (
      <>
        {displayedSkills.map((skill, index) => (
          <span key={index} className="upskill-soft-skill-tag-unique">
            {skill}
          </span>
        ))}
        {remainingCount > 0 && (
          <span
            className="upskill-soft-skill-tag-unique"
            style={{ fontStyle: "italic", opacity: 0.7 }}
          >
            +{remainingCount} {t("employees.results.more")}
          </span>
        )}
      </>
    );
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  // ADDED: State for filter panel collapse/expand
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    getDepartmentsDropDownApiHandler();
  }, []);

  const [upskillDataLength, setUpskillDataLength] = useState(null);
  // const [itemsPerPage] = useState(21); // 20 items per page // Pagination
  const [itemsPerPage, setItemsPerPage] = useState(12); // Pagination
  // EmployeesPage-style isAllSelected (based on total result count)
  const isAllSelected =
    (upskillDataLength ?? 0) > 0 &&
    selectedIds.length === (upskillDataLength ?? 0);
  useEffect(() => {
    handleFilterData();
  }, [
    currentPage,
    searchTerm,
    filterDepartment,
    filterUrgency,
    sortBy,
    itemsPerPage,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterDepartment, filterUrgency, sortBy, itemsPerPage]);
  // clamp current page if total shrinks (e.g., filter change)
  useEffect(() => {
    const newTotalPages = Math.max(
      1,
      Math.ceil((upskillDataLength || 0) / itemsPerPage),
    );
    if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
  }, [upskillDataLength, itemsPerPage]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // const totalPages = Math.ceil(upskillDataLength / itemsPerPage);
  const totalPages = Math.max(
    1,
    Math.ceil((upskillDataLength || 0) / itemsPerPage),
  );
  const start = (upskillDataLength ?? 0) === 0 ? 0 : indexOfFirstItem + 1;
  const end =
    (upskillDataLength ?? 0) === 0
      ? 0
      : Math.min(indexOfFirstItem + upskillData.length, upskillDataLength);
  const normalizeUpskillResponse = (response) => {
    /*
     * Supports all of these formats:
     *
     * 1. Direct array:
     *    [{...}, {...}]
     *
     * 2. Axios response:
     *    { data: [{...}, {...}] }
     *
     * 3. Wrapped response:
     *    { data: [{...}], totalCount: 10 }
     *
     * 4. Nested wrapped response:
     *    { data: { data: [{...}], totalCount: 10 } }
     */

    const payload = response?.data ?? response;

    let records = [];

    if (Array.isArray(payload)) {
      records = payload;
    } else if (Array.isArray(payload?.data)) {
      records = payload.data;
    } else if (Array.isArray(payload?.content)) {
      records = payload.content;
    } else if (Array.isArray(payload?.results)) {
      records = payload.results;
    }

    const receivedTotal =
      payload?.totalCount ??
      payload?.totalElements ??
      response?.totalCount ??
      response?.totalElements;

    const parsedTotal = Number(receivedTotal);

    return {
      records,
      totalCount: Number.isFinite(parsedTotal) ? parsedTotal : records.length,
    };
  };
  const handleFilterData = async () => {
    try {
      setIsLoading(true);

      const englishUrgency = getEnglishUrgencyValues(filterUrgency);

      const obj = {
        pageNo: currentPage,
        pageSize: itemsPerPage,
        searchFilter: searchTerm,
        department: filterDepartment,
        urgency: englishUrgency,
        sortBy,
        userId: paramValueUserId || null,
      };

      console.log("Upskilling API payload:", obj);

      const response = await getUpSkillingApi(obj);

      console.log("Raw getUpSkillingApi response:", response);

      const { records, totalCount } = normalizeUpskillResponse(response);

      console.log("Normalized upskill records:", records);
      console.log("Normalized total count:", totalCount);

      setUpskillData(records);
      setUpskillDataLength(totalCount);
    } catch (error) {
      console.error("Error fetching upskill data:", error);

      setUpskillData([]);
      setUpskillDataLength(0);

      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);

    console.log("Fetching data for page:", currentPage);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const [departments, setDepartments] = useState([]);
  const getDepartmentsDropDownApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();

      setDepartments([{ name: t("common.all"), _id: "All" }, ...response.data]);
      console.log("Departments fetched:", departments);
    } catch (error) {
      console.error("Error fetching departments:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const [mockLearningPath, setMockLearningPath] = useState([]);
  const [activeSkillTab, setActiveSkillTab] = useState("job-skills");
  const [loadingPersonId, setLoadingPersonId] = useState(null);

  const getLearningPathApiHandler = async (person) => {
    setSelectedPerson(person);

    console.log("clicked getLearningPathApi");

    setLoadingPersonId(person._id); // lock only this button
    try {
      const response = await getLearningPathApi({
        userId: person._id,
      });
      setShowLearningPathModal(true);
      document.body.classList.add("learning-path-modal-open");
      setMockLearningPath(response.data.learningPath || []);

      console.log("Learning path response:", response);
    } catch (error) {
      setLoadingPersonId(null);
      handleApiError(error, {
        showModal: false,
      });
    } finally {
      setLoadingPersonId(null); // release only this button
    }
  };

  // Build buckets once
  const jobSkills = (mockLearningPath || []).filter(
    (s) => s.type === "Job Skill",
  );
  const softSkills = (mockLearningPath || []).filter(
    (s) => s.type === "Soft Skill",
  );
  const noSkillsInBothTabs = jobSkills.length === 0 && softSkills.length === 0;

  // Active-tab view
  const filteredSkills =
    activeSkillTab === "job-skills" ? jobSkills : softSkills;

  // i18n-friendly copy with a safe default
  const allGoodCopy = t(
    "upskillPage.learningPathModal.allSkillsUpToDate",
    "All required skills are up to date. No course recommendations are needed at this time.",
  );

  // const handleDownloadPDF = async () => {
  //   console.log("clicked download pdf");
  //   try {
  //
  //     const response = await downloadLearningPathPdfApi(obj);

  //     console.log("PDF download response:", response);
  //   } catch (error) {
  //     console.error("Error downloading PDF:", error);
  //     handleApiError(error, {
  //       showModal: true,

  //       type: "error",
  //       title: "Failed",
  //     });
  //   }
  // };
  const [downloading, setDownloading] = useState(false);
  // component.tsx
  const handleDownloadPDF = async () => {
    if (downloading) return; // guard against double-clicks
    setDownloading(true);
    try {
      const obj = { userId: selectedPerson._id };
      const res = await downloadLearningPathPdfApi(obj); // AxiosResponse

      const contentType = res.headers?.["content-type"] ?? "application/pdf";
      const blob = new Blob([res.data], { type: contentType });

      const cd = res.headers?.["content-disposition"];
      const filename =
        getFileNameFromContentDisposition(cd) ||
        `learning-path-${selectedPerson.userId}.pdf`;

      downloadBlob(blob, filename);
      console.log("PDF download response:", res.status);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      handleApiError(error, {
        showModal: true,
        type: "error",
        title: t("common.failed"),
        message: t("upskillPage.learningPathModal.downloadFailed"),
      });
    } finally {
      setDownloading(false);
    }
  };

  // --- helpers ---
  function getFileNameFromContentDisposition(cd) {
    if (!cd) return undefined;
    // supports both filename*= and filename=
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
    const raw = m?.[1] || m?.[2];
    return raw ? decodeURIComponent(raw) : undefined;
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const shareLearningPathHandler = async () => {
    try {
      setLoadingPersonId(selectedPerson._id); // lock this button

      const obj = { userIdArray: [selectedPerson._id] };
      const response = await shareLearningPathInsidePeople(obj);

      if (response?.code === 200) {
        MessageManager.showToast(
          "success",
          t("upskillPage.learningPathModal.shareSuccessToast"),
        );
        setLoadingPersonId(null); // ✅ only unlock on success
      } else {
        throw new Error(response?.message || t("common.unexpectedResponse"));
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
        type: "error",
        title: t("common.failed"),
        message: t("upskillPage.learningPathModal.sharingFailed"),
      });
    } finally {
      setLoadingPersonId(null); // ✅ re-enable even on error so user can retry
    }
  };

  const sanitizeSearch = (val) =>
    val.replace(/[\\`~!@#$%^&*()+={}[\]|:;"'<>,.?/]/g, "");

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case "beginner":
        return "course-level-beginner";
      case "intermediate":
        return "course-level-intermediate";
      case "advanced":
        return "course-level-advanced";
      default:
        return "course-level-default";
    }
  };

  const handleBulkShare = async () => {
    try {
      const obj = {
        userIdArray: selectedIds,
      };
      const response = await shareLearningPathInsidePeople(obj);
      if (response?.code === 200) {
        MessageManager.showToast(
          "success",
          t("upskillPage.learningPathModal.shareSuccessToast", {
            count: selectedIds.length,
          }),
        );
        setSelectedIds([]);
      } else {
        throw new Error(response?.message || t("common.unexpectedResponse"));
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
        type: "error",
        title: t("common.failed"),
        message: t("upskillPage.learningPathModal.sharingFailed"),
      });
    } finally {
      setLoadingPersonId(null);
    }
  };
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    // avoid tooltip popups on mobile long-press
    setIsTouch(
      typeof window !== "undefined" &&
        ("ontouchstart" in window || navigator.maxTouchPoints > 0),
    );
  }, []);

  useEffect(() => {
    setSelectedIds([]);
  }, [searchTerm, filterDepartment, filterUrgency, sortBy, paramValueUserId]);

  const renderStarRating = (rating) => {
    const numRating = Number.parseFloat(rating) || 0;
    const fullStars = Math.floor(numRating);
    const hasHalfStar = numRating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="course-rating-stars">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} size={12} className="star-filled" />
        ))}
        {hasHalfStar && <Star size={12} className="star-half" />}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} size={12} className="star-empty" />
        ))}
        <span className="rating-number">({numRating})</span>
      </div>
    );
  };

  // ADDED: Function to count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterDepartment && filterDepartment !== "All") count++;
    if (
      filterUrgency &&
      filterUrgency.length > 0 &&
      !filterUrgency.includes("All")
    )
      count++;
    if (sortBy && sortBy !== "Urgency") count++;
    return count;
  };

  const handleSearchButtonClick = () => {
    setCurrentPage(1);
    setSearchTerm(searchInputValue.trim());
    setShowSearchBar(false);
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <div className="upskill-page">
      <BackButton />
      <main className="upskill-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <TrendingUp size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("upskillPage.pageTitle")}</h1>
              <CustomTooltip
                tooltip={{
                  title: t("upskillPage.headerTooltip.title"),
                  message: t("upskillPage.headerTooltip.message"),
                }}
                id="upskill-page-header-tooltip"
              >
                <div
                  style={{
                    cursor: "help",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Info
                    size={18}
                    style={{
                      marginLeft: "1px",
                      marginBottom: "4px",
                      color: "var(--text-secondary)",
                      cursor: "pointer",
                    }}
                  />
                </div>
              </CustomTooltip>
            </div>
            <p>{t("upskillPage.pageDescription")}</p>
          </div>

          {/* Smart Search & Filter panel (same as Employees page) */}
          <div className="smart-filter-panel-upskill">
            <div className="smart-filter-top-bar">
              {/* Left side (badge / filters applied) */}
              <div className="smart-filter-left">
                <div className="smart-filter-info">
                  {getAppliedFiltersCount() > 0 && (
                    <>
                      <span className="smart-filter-badge">
                        {getAppliedFiltersCount()}
                      </span>
                      <span>{t("common.filtersApplied")}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Right side (search input + icons pinned right always) */}
              <div className="smart-filter-right">
                {showSearchBar && (
                  <div className="smart-search-inline-row smart-search-right">
                    <input
                      type="text"
                      placeholder={t("upskillPage.searchPlaceholder")}
                      value={searchInputValue}
                      onChange={(e) =>
                        setSearchInputValue(sanitizeSearch(e.target.value))
                      }
                      className="smart-search-inline-input"
                      autoFocus
                    />
                    <span className="mobile-search-placeholder-hint">
                      {t("upskillPage.searchPlaceholder")}
                    </span>

                    {/* close (same as your current behavior) */}
                    <button
                      className="smart-filter-icon-btn active"
                      title={t("common.search")}
                      onClick={handleSearchButtonClick}
                    >
                      <Search size={18} />
                    </button>

                    {searchTerm && (
                      <button
                        className="smart-search-clear"
                        onClick={handleClearSearch}
                        title={t("common.clear")}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                )}

                {/* Icons pinned to the right */}
                <div className="smart-filter-icons-right">
                  {!showSearchBar && (
                    <button
                      className={`smart-filter-icon-btn ${showSearchBar ? "active" : ""}`}
                      onClick={() => {
                        setSearchInputValue(searchTerm);
                        setShowSearchBar(true);
                        setShowFiltersPanel(false);
                      }}
                      title={t("common.search")}
                    >
                      <Search size={18} />
                    </button>
                  )}

                  <div className="smart-filter-separator" />

                  <button
                    className={`smart-filter-icon-btn ${showFiltersPanel ? "active" : ""}`}
                    onClick={() => {
                      setShowFiltersPanel(!showFiltersPanel);
                      if (!showFiltersPanel) setShowSearchBar(false);
                    }}
                    title={t("common.filters")}
                  >
                    <Filter size={18} />
                  </button>
                </div>
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>{t("upskillPage.filters.department")}</label>
                      <select
                        value={filterDepartment}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigator("/settings/organization-info");
                            return;
                          }
                          setFilterDepartment(e.target.value);
                        }}
                        className={`smart-filter-select ${
                          filterDepartment !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {departments.length === 0 ? (
                          <>
                            <option value="">
                              {t("upskillPage.filters.noDepartmentsAvailable")}
                            </option>
                            <option value="__add__">
                              {t("upskillPage.filters.createDepartment")}
                            </option>
                          </>
                        ) : (
                          <>
                            {/* <option value="">Select department</option> */}
                            {departments.map((dept) => (
                              <option key={dept._id} value={dept.name}>
                                {dept.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("upskillPage.filters.sortBy")}</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className={`smart-filter-select ${
                          sortBy !== "Urgency" ? "smart-active" : ""
                        }`}
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("upskillPage.filters.urgency")}</label>
                      <CustomReadinessFilter
                        options={urgencyLevels}
                        selectedValues={(filterUrgency.length
                          ? filterUrgency
                          : ["All"]
                        ).map(getUrgencyDisplayLabel)}
                        onSelect={handleUrgencySelect}
                        onRemove={handleUrgencyRemove}
                        label={t("upskillPage.filters.urgency")}
                        getTooltip={UpskillUrgencyTooltip}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="u-page-actions">
            {/* Select All beside bulk share (ADD THIS) */}
            {/* style={{ fontSize: 16, display: "flex", alignItems: "center" }} */}
            <label className="select-all-wrap">
              <input
                type="checkbox"
                style={{ width: 15, height: 15, transform: "scale(1.1)" }}
                checked={isAllSelected}
                disabled={selectAllLoading || (upskillDataLength ?? 0) === 0}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              />
              <span>
                {selectAllLoading
                  ? t("common.selecting")
                  : t("common.selectAll")}
                {!!upskillDataLength &&
                  !selectAllLoading &&
                  isAllSelected &&
                  ` (${upskillDataLength})`}
              </span>
            </label>

            <button
              className="btn btn-primary up-bulkshare-btn"
              onClick={handleBulkShare}
              disabled={selectedIds.length === 0}
              // Title only for non-touch (desktop hover). Prevents the ugly mobile tooltip.
              title={
                isTouch
                  ? undefined
                  : selectedIds.length === 0
                    ? t("common.selectAtLeastOnePerson")
                    : t("common.shareWithSelected", {
                        count: selectedIds.length,
                      })
              }
              aria-disabled={selectedIds.length === 0}
              aria-label={
                selectedIds.length === 0
                  ? t("common.bulkShareLearningPathsDisabled")
                  : t("common.bulkShareLearningPathsWithSelected", {
                      count: selectedIds.length,
                    })
              }
            >
              <Send size={16} />
              <span className="bulkshare-text bulkshare-text--full">
                {t("upskillPage.bulkshare")}
              </span>
              {selectedIds.length > 0 && (
                <span className="bulkshare-count">({selectedIds.length})</span>
              )}
            </button>
          </div>

          {isLoading ? (
            <div className="upskill-grid" aria-busy="true">
              {Array.from({ length: Math.min(itemsPerPage, 12) }).map(
                (_, i) => (
                  <div key={i} className="upskill-card skeleton-card">
                    <div className="card-header skeleton-header" />
                    <div className="upskill-needs-section-unique">
                      <div className="skeleton-line short" />
                      <div className="skeleton-line" />
                      <div className="skeleton-line" />
                    </div>
                    <div className="skeleton-button" />
                  </div>
                ),
              )}
            </div>
          ) : upskillData?.length === 0 ? (
            getAppliedFiltersCount() > 0 ? (
              <FilterNoResults
                title={t("common.noResults")}
                message={t("common.adjustOrClear")}
                clearLabel={t("common.clearFilters")}
                onClear={clearAllFilters}
              />
            ) : (
              <EmptyState
                icon={TrendingUp}
                iconSize={48}
                title={t("upskillPage.emptyTitle")}
                message={t("upskillPage.emptyMessage")}
                actionLabel={t("common.goToEmployees")}
                onAction={() => navigator("/employees")}
              />
            )
          ) : (
            <div className="upskill-grid">
              {sortData(upskillData).map((person) => (
                <div key={person._id} className="upskill-card">
                  <label
                    className="card-select"
                    onClick={(e) => e.stopPropagation()}
                    title={t("common.selectThisPerson")}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(person._id)}
                      onChange={(e) => toggleOne(person._id, e.target.checked)}
                    />
                  </label>
                  <div className="upskill-card-content-wrapper-unique">
                    <div className="card-header">
                      <div className="person-info">
                        <div className="person-avatar">
                          <User size={20} />
                        </div>
                        <div onClick={() => handlePersonClick(person)}>
                          <h3>{person.name}</h3>
                          <p className="role">{person.designation}</p>
                          <p className="department">{person.department}</p>
                        </div>
                      </div>

                      <CustomTooltip
                        tooltip={getPriorityTooltip(person.skillScore)}
                        id={`priority-${person._id}`}
                      >
                        <span
                          className={`urgency-badge ${getUrgencyColor(
                            person.skillScore,
                          )}`}
                        >
                          <Clock size={12} />
                          {getUrgencyCriteria(person.skillScore)}
                        </span>
                      </CustomTooltip>
                    </div>

                    <div className="upskill-needs-section-unique">
                      <h4>{t("upskillPage.needsUpskilling")}</h4>

                      <div className="upskill-skills-layer-unique">
                        <div className="upskill-layer-title-unique">
                          <Briefcase size={12} />
                          {t("upskillPage.jobSkills")}
                        </div>
                        <div
                          className="upskill-skills-tags-unique"
                          onClick={() => {
                            handleViewMore(person.hardSkillsGap);
                          }}
                        >
                          {renderSkillsWithLimit(person.hardSkillsGap)}
                        </div>
                      </div>

                      <div className="upskill-skills-layer-unique">
                        <div className="upskill-layer-title-unique">
                          <User size={12} />
                          {t("upskillPage.softSkills")}
                        </div>
                        <div
                          className="upskill-skills-tags-unique"
                          onClick={() => {
                            handleViewMore(
                              person.softSkillsGap,
                              t("upskillPage.viewMore.softSkillsTitle"),
                            );
                          }}
                        >
                          {renderSoftSkillsWithLimit(person.softSkillsGap)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    key={person._id}
                    className="btn btn-primary assign-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      getLearningPathApiHandler(person);
                    }}
                    disabled={loadingPersonId === person._id}
                  >
                    {loadingPersonId === person._id ? (
                      <>
                        <div className="onboarding-wizard-btn-spinner"></div>
                        {t("onboarding.fetching")}
                      </>
                    ) : (
                      <>{t("upskillPage.assignTraining")}</>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {!isLoading &&
            (upskillDataLength ?? 0) > 0 &&
            (upskillData?.length ?? 0) > 0 && (
              <Pagination
                loading={isLoading}
                total={upskillDataLength}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                currentPageCount={upskillData?.length}
                onPageChange={(page) => setCurrentPage(page)}
                pageSizeOptions={[12, 24, 60, 120]}
                onPageSizeChange={(size) => {
                  setItemsPerPage(size);
                  const newTotalPages = Math.max(
                    1,
                    Math.ceil((upskillDataLength || 0) / size),
                  );
                  if (currentPage > newTotalPages)
                    setCurrentPage(newTotalPages);
                }}
                infoRenderer={({ start, end, total }) =>
                  t("mobilityPage.paginationInfo", { start, end, total })
                }
              />
            )}
        </div>
      </main>
      {/* Learning Path Modal */}
      {showLearningPathModal && selectedPerson && (
        <div className="learning-path-modal-overlay">
          <div className="learning-path-modal">
            <div className="learning-path-modal-header">
              <div className="modal-header-content">
                <div className="modal-title-section">
                  <div className="upskill-modal-icon">
                    <Target size={24} />
                  </div>
                  <div>
                    <h2>{t("upskillPage.learningPathModal.title")}</h2>
                    <p>
                      {t("upskillPage.learningPathModal.description", {
                        name: selectedPerson.name,
                      })}
                    </p>
                  </div>
                </div>
                <div className="modal-actions">
                  {/* <button
                    onClick={() => shareLearningPathHandler()}
                    className="download-btn"
                    title={t("upskillPage.learningPathModal.downloadPdf")}
                  >
                    <Send size={18} />
                    {t("upskillPage.learningPathModal.sharePdf")}{" "}
                    {selectedPerson.name}
                  </button> */}
                  {/* <button className="download-btn" onClick={handleDownloadPDF}>
                    <Download size={18} /> Learning path
                  </button> */}
                  <button
                    className="download-btn"
                    onClick={handleDownloadPDF}
                    disabled={downloading}
                    aria-busy={downloading}
                    aria-live="polite"
                  >
                    {downloading ? (
                      <>
                        <span className="btn-spinner" aria-hidden="true" />{" "}
                        {t("upskillPage.learningPathModal.downloading")}
                      </>
                    ) : (
                      <>
                        <Download size={18} /> &nbsp;{" "}
                        {t(
                          "upskillPage.learningPathModal.downloadLearningPath",
                        )}
                      </>
                    )}
                  </button>

                  <button
                    onClick={shareLearningPathHandler}
                    className="download-btn"
                    title={t("upskillPage.learningPathModal.downloadPdf")}
                    disabled={loadingPersonId === selectedPerson.userId} // disable while loading
                  >
                    {loadingPersonId === selectedPerson.userId ? (
                      <Loader2 size={18} className="spin" aria-hidden />
                    ) : (
                      <Send size={18} />
                    )}
                    <span className="btn-label" style={{ marginLeft: 8 }}>
                      {t("upskillPage.learningPathModal.sharePdf")}{" "}
                      {selectedPerson.name}
                    </span>
                  </button>
                  <button
                    className="close-btn"
                    onClick={() => {
                      setShowLearningPathModal(false);
                      setMockLearningPath([]);
                      document.body.classList.remove(
                        "learning-path-modal-open",
                      );
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            </div>

            <div className="learning-path-content">
              <div className="upskill-tabs-container">
                <div className="upskill-tabs-header">
                  <button
                    className={`upskill-tab-button ${
                      activeSkillTab === "job-skills"
                        ? "upskill-tab-active"
                        : ""
                    }`}
                    onClick={() => setActiveSkillTab("job-skills")}
                  >
                    <Briefcase size={16} />
                    {t("upskillPage.jobSkills")}
                  </button>
                  <button
                    className={`upskill-tab-button ${
                      activeSkillTab === "soft-skills"
                        ? "upskill-tab-active"
                        : ""
                    }`}
                    onClick={() => setActiveSkillTab("soft-skills")}
                  >
                    <User size={16} />
                    {t("upskillPage.softSkills")}
                  </button>
                </div>
              </div>

              <div className="skills-recommendations">
                {noSkillsInBothTabs ? (
                  <div
                    className="no-recs-message"
                    role="status"
                    aria-live="polite"
                    style={{
                      padding: "16px",
                      border: "1px solid #e5e7eb",
                      background: "#f8fafc",
                      borderRadius: 12,
                      color: "var(--text-secondary)",
                      textAlign: "center",
                    }}
                  >
                    {allGoodCopy}
                  </div>
                ) : filteredSkills.length ? (
                  filteredSkills.map((skill, index) => (
                    <div key={index} className="skill-recommendation-card">
                      <div className="skill-header">
                        <div className="skill-info">
                          <h3>{skill.skill_gap}</h3>
                          {/*<div className="skill-meta">
                              <span
                                className={`skill-type-badge ${
                                  skill.type === "Job Skill" ? "job-skill" : "soft-skill"
                                }`}
                              >
                                {skill.type === "Job Skill" ? <Briefcase size={14} /> : <User size={14} />}
                                {skill.type}
                              </span>
                            </div>*/}
                        </div>
                      </div>

                      {/* Recommended Courses Section */}
                      {skill.recommended_courses.length > 0 && (
                        <div className="courses-section">
                          <h4>
                            <BookOpen size={16} />
                            {t("upskillPage.recommendedCourses")}
                          </h4>
                          <div className="courses-grid-improved">
                            {skill.recommended_courses.map(
                              (course, courseIndex) => (
                                <div
                                  key={courseIndex}
                                  className="course-card-improved"
                                >
                                  <div className="course-card-header">
                                    <div className="course-icon">
                                      <PlayCircle size={20} />
                                    </div>
                                    <div className="course-level-badge">
                                      <Award size={12} />
                                      <span
                                        className={`level-text ${getLevelColor(
                                          course.level,
                                        )}`}
                                      >
                                        {course.level ||
                                          t(
                                            "upskillPage.learningPathModal.beginner",
                                          )}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="course-content">
                                    <h5 className="course-title">
                                      {course.course_name}
                                    </h5>

                                    <div className="course-rating">
                                      {renderStarRating(course.rating)}
                                    </div>
                                  </div>

                                  <div
                                    className="course-footer"
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      paddingTop: "16px",
                                      borderTop: "1px solid #e5e7eb",
                                    }}
                                  >
                                    <span
                                      className="course-platform-tag"
                                      style={{
                                        fontSize: "12px",
                                        color: "var(--text-secondary)",
                                        background: "#f1f5f9",
                                        padding: "4px 8px",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      {course.platform ||
                                        t(
                                          "upskillPage.trainingModal.platformPlaceholder",
                                        )}
                                    </span>
                                    <a
                                      href={course.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn-primary course-link-btn"
                                    >
                                      <ExternalLink size={14} />
                                      {t("common.view")}
                                    </a>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                      {/* Learning Steps Section */}
                      <div className="steps-section">
                        <h4>
                          <Target size={16} />
                          {t("upskillPage.learningPathModal.learningJourney")}
                        </h4>
                        <div className="steps-timeline">
                          {skill.steps.map((step, stepIndex) => (
                            <div key={stepIndex} className="step-item">
                              <div className="step-indicator">
                                <div className="step-number">
                                  {stepIndex + 1}
                                </div>
                                <div className="step-stage">{step.stage}</div>
                              </div>
                              <div className="step-content">
                                <p>{step.description}</p>
                              </div>
                              {/*stepIndex < skill.steps.length - 1 && (
                                  <ArrowRight className="step-arrow" size={16} />
                                )*/}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Note Section */}
                      {skill.note && (
                        <div className="note-section">
                          <div className="note-header">
                            <AlertCircle size={16} />
                            <span>
                              {t("upskillPage.learningPathModal.importantNote")}
                            </span>
                          </div>
                          <p>{skill.note}</p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div
                    className="no-recs-message"
                    role="status"
                    aria-live="polite"
                    style={{
                      padding: "16px",
                      border: "1px solid #e5e7eb",
                      background: "#f8fafc",
                      borderRadius: 12,
                      color: "var(--text-secondary)",
                      textAlign: "center",
                    }}
                  >
                    {activeSkillTab === "job-skills"
                      ? t(
                          "upskillPage.learningPathModal.noJobSkillsNeedUpskilling",
                        )
                      : t(
                          "upskillPage.learningPathModal.noSoftSkillsNeedUpskilling",
                        )}
                  </div>
                )}
              </div>
            </div>

            {/*<div className="learning-path-footer">
              <div className="footer-note">
                <Lightbulb size={16} />
                <span>{t("upskillPage.learningPathModal.noteMessage")}</span>
              </div>
            </div>*/}
          </div>
        </div>
      )}

      <ViewMoreModal
        open={show}
        onClose={() => setShow(false)}
        title={ViewMoreModalTitle}
        skills={selectedSkills}
        variant="job-skills"
        type="hard"
        onRemoveSkill={(skill) =>
          setSelectedSkills((s) => s.filter((x) => x !== skill))
        }
      />

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            person: "",
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={showModal.showButton}
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default UpskillPage;
